/**
 * Endpoint type definitions for multi-platform dice roll destinations
 *
 * Supports: Foundry VTT, Roll20, Discord, Owlbear Rodeo, D&D Beyond Maps
 */

/**
 * Supported endpoint platform types
 */
export type EndpointType = 'foundry' | 'roll20' | 'discord' | 'owlbear' | 'dndbeyond' | 'standalone' | 'player';

/**
 * Connection status for active endpoints
 */
export type ConnectionStatus = 'disconnected' | 'connecting' | 'connected' | 'error';

/**
 * Foundry VTT connection credentials
 */
export interface FoundryCredentials {
  serverUrl: string;
  sessionCode: string;
  playerName: string;
}

/**
 * Roll20 connection credentials (future implementation)
 */
export interface Roll20Credentials {
  // To be defined when implementing Roll20 integration
  apiKey?: string;
  campaignId?: string;
  playerName?: string;
}

/**
 * Discord connection credentials
 * Bot token is managed server-side; client only stores channel/guild identifiers
 */
export interface DiscordCredentials {
  channelId: string;
  guildId: string;
  guildName: string;
  channelName: string;
}

/**
 * Owlbear Rodeo connection credentials (future implementation)
 */
export interface OwlbearCredentials {
  // To be defined when implementing Owlbear integration
  roomId?: string;
  playerName?: string;
}

/**
 * D&D Beyond Maps connection credentials (future implementation)
 */
export interface DNDBeyondCredentials {
  // To be defined when implementing D&D Beyond integration
  campaignId?: string;
  characterId?: string;
  accessToken?: string;
}

/**
 * Standalone session credentials (no VTT required)
 */
export interface StandaloneCredentials {
  sessionCode: string;
  hostName: string;
}

/**
 * Player session credentials (join an existing session from phone)
 */
export interface PlayerCredentials {
  sessionCode: string;
  playerName: string;
}

/**
 * Union type for all endpoint credentials
 */
export type EndpointCredentials =
  | FoundryCredentials
  | Roll20Credentials
  | DiscordCredentials
  | OwlbearCredentials
  | DNDBeyondCredentials
  | StandaloneCredentials
  | PlayerCredentials;

/**
 * Metadata tracked for each endpoint
 */
export interface EndpointMetadata {
  createdAt: string;
  lastUsedAt: string;
  totalRollsSubmitted: number;
}

/**
 * Saved endpoint configuration (persistent)
 */
export interface SavedEndpoint {
  id: string;
  name: string;
  type: EndpointType;
  icon?: string;
  credentials: EndpointCredentials;
  metadata: EndpointMetadata;
}

/**
 * Active connection state (ephemeral)
 */
export interface ConnectionState {
  status: ConnectionStatus;
  connectedAt?: Date;
  sessionData?: any;
  lastError?: string;
}

/**
 * Type guard to check if credentials are for Foundry VTT
 */
export function isFoundryCredentials(
  credentials: EndpointCredentials
): credentials is FoundryCredentials {
  return (
    'serverUrl' in credentials &&
    'sessionCode' in credentials &&
    'playerName' in credentials
  );
}

/**
 * Type guard to check if endpoint is Foundry VTT
 */
export function isFoundryEndpoint(endpoint: SavedEndpoint): boolean {
  return endpoint.type === 'foundry';
}

/**
 * Type guard to check if credentials are for Discord
 */
export function isDiscordCredentials(
  credentials: EndpointCredentials
): credentials is DiscordCredentials {
  return (
    'channelId' in credentials &&
    'guildId' in credentials &&
    'guildName' in credentials &&
    'channelName' in credentials
  );
}

/**
 * Type guard to check if endpoint is Discord
 */
export function isDiscordEndpoint(endpoint: SavedEndpoint): boolean {
  return endpoint.type === 'discord';
}

/**
 * Type guard to check if credentials are for Standalone mode
 */
export function isStandaloneCredentials(
  credentials: EndpointCredentials
): credentials is StandaloneCredentials {
  return (
    'sessionCode' in credentials &&
    'hostName' in credentials
  );
}

/**
 * Type guard to check if endpoint is Standalone
 */
export function isStandaloneEndpoint(endpoint: SavedEndpoint): boolean {
  return endpoint.type === 'standalone';
}

/**
 * Type guard to check if credentials are for Player mode
 */
export function isPlayerCredentials(
  credentials: EndpointCredentials
): credentials is PlayerCredentials {
  return (
    'sessionCode' in credentials &&
    'playerName' in credentials
  );
}

/**
 * Type guard to check if endpoint is Player
 */
export function isPlayerEndpoint(endpoint: SavedEndpoint): boolean {
  return endpoint.type === 'player';
}

/**
 * Create initial metadata for new endpoint
 */
export function createInitialMetadata(): EndpointMetadata {
  const now = new Date().toISOString();
  return {
    createdAt: now,
    lastUsedAt: now,
    totalRollsSubmitted: 0
  };
}

/**
 * Generate unique endpoint ID
 */
export function generateEndpointId(): string {
  return `endpoint-${Date.now()}-${Math.random().toString(36).substring(2, 9)}`;
}
